import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RootState } from '../store';
import { setDashboardCards, setMetrics } from '../store/dashboardSlice';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, Breakpoints, FontStacks, AIAnimations, NeuralGrid } from '../constants/theme';
import ModernQuickFilters from '../components/filters/ModernQuickFilters';
import EnhancedFilterDrawer from '../components/filters/EnhancedFilterDrawer';
import DashboardCard from '../components/DashboardCard';
import InteractiveDashboardCard from '../components/InteractiveDashboardCard';
import { KpiDrillDownCard, CleanDrillDownModal } from '../components/drilldown';
import { KpiType } from '../types/drilldown';
import LoadingSpinner from '../components/LoadingSpinner';
import { DashboardGridSkeleton } from '../components/SkeletonLoader';
import { NoDataEmptyState } from '../components/EmptyState';
import { DashboardCard as DashboardCardType, TalentMetrics } from '../types';
import { AccessibilityUtils } from '../utils/accessibility';
import { AnimatedCounter, InteractiveButton, ProgressRing } from '../components/MicroInteractions';
import DataExportService from '../utils/dataExport';
import DataConsistencyService from '../services/DataConsistencyService';
import { ResponsiveUtils, responsiveTypography } from '../utils/responsive';
import RecommendationsModal from '../components/RecommendationsModal';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import { filterDashboardCards, filterTalentMetrics, getRoleDashboardConfig, getRoleWelcomeMessage } from '../utils/roleBasedAccess';
import { RealTimeSyncIndicator } from '../components/RealTimeSyncIndicator';
import TalentAcquisitionWidget from '../components/insights/TalentAcquisitionWidget';
import HiringTeamPanel from '../components/insights/HiringTeamPanel';

// Ultra-Modern Professional Canvas
const PowerBICanvas = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  position: relative;
  padding: 0;
  overflow-x: hidden;
  font-family: ${FontStacks.primary};
  
  /* Professional container layout */
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: ${Spacing.xxxl}px ${Spacing.xl}px;
  }
`;

const ReportSection = styled.section`
  margin-bottom: ${Spacing.xxxxl}px;
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${Spacing.xxxl}px;
  padding-bottom: ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
`;

const SectionTitle = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.display}px;
  font-weight: ${FontWeights.semibold};
  margin: 0;
  color: ${Colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  /* Ultra-clean underline */
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${Colors.secondary};
    border-radius: 1px;
  }
`;


const SectionActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${Colors.textTertiary};
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  position: relative;
  
  &:hover {
    color: ${Colors.textPrimary};
    background: ${Colors.surfaceHover};
  }
  
  &:focus {
    outline: none;
    color: ${Colors.secondary};
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

// Ultra-Modern Grid System
const PowerBITilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: ${Spacing.lg}px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.lg}px;
  }
`;

const PowerBITile = styled.div<{ span?: number; height?: number }>`
  grid-column: span ${props => props.span || 1};
  grid-row: span ${props => props.height || 1};
  min-height: 140px;
`;

// Ultra-Modern Insight Card
const PowerBIInsightCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  position: relative;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-2px);
  }
`;

const PowerBIChart = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  transition: all 200ms ease;
  
  &:hover {
    border-color: ${Colors.borderAccent};
    box-shadow: ${Shadows.lg};
    transform: translateY(-1px);
  }
`;

const ChartTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.xxl}px 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;


const InsightHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.xl}px;
`;

const InsightBadge = styled.span`
  background-color: ${Colors.secondary};
  color: ${Colors.textOnPrimary};
  padding: ${Spacing.xs}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InsightPriority = styled.span`
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InsightTitle = styled.h3`
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.lg}px;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const InsightDescription = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${Spacing.xl}px;
  margin: 0;
`;

const InsightAction = styled.button`
  background: none;
  border: none;
  color: ${Colors.secondary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  padding: ${Spacing.sm}px 0;
  margin-top: ${Spacing.lg}px;
  transition: all 150ms ease;
  
  &:hover {
    color: ${Colors.accent};
  }
  
  &:focus {
    outline: none;
    color: ${Colors.accent};
  }
`;


const TrendItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.lg}px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TrendLabel = styled.span`
  flex: 1;
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  font-weight: ${FontWeights.medium};
`;

const TrendBar = styled.div`
  flex: 2;
  height: 6px;
  background-color: ${Colors.borderLight};
  border-radius: ${BorderRadius.xs}px;
  margin: 0 ${Spacing.lg}px;
  overflow: hidden;
`;

const TrendProgress = styled.div<{ width: string; color: string }>`
  height: 100%;
  width: ${props => props.width};
  background-color: ${props => props.color};
  border-radius: ${BorderRadius.xs}px;
  transition: width 0.6s ease;
`;

const TrendValue = styled.span`
  min-width: 50px;
  text-align: right;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  font-family: ${FontStacks.mono};
`;


const DashboardScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentRole, userName, department } = useSelector((state: RootState) => state.user);
  const { dashboardCards, metrics, loading } = useSelector((state: RootState) => state.dashboard);
  const [isMobile, setIsMobile] = useState(ResponsiveUtils.isMobile());
  const [modalOpen, setModalOpen] = useState(false);
  const [drilldownModal, setDrilldownModal] = useState<{ open: boolean; kpiType: KpiType | null; value: number | string }>({ open: false, kpiType: null, value: '' });
  const [selectedInsight, setSelectedInsight] = useState<{
    title: string;
    recommendations: Array<{
      id: string;
      title: string;
      description: string;
      priority: 'high' | 'medium' | 'low';
      timeline: string;
      impact: string;
    }>;
  } | null>(null);

  // Get role-based dashboard configuration
  const dashboardConfig = getRoleDashboardConfig(currentRole);
  const welcomeMessage = getRoleWelcomeMessage(currentRole, userName);

  useEffect(() => {
    loadDashboardData();
    
    // Handle responsive changes
    const handleResize = () => setIsMobile(ResponsiveUtils.isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentRole]);

  const loadDashboardData = () => {
    const mockMetrics: TalentMetrics = {
      totalEmployees: DataConsistencyService.getTotalEmployees(),
      attritionRate: 8.2,
      engagementScore: 8.7,
      leadershipReadiness: 73,
      timeToFill: 31,
      performanceTrend: 4.2,
      hiringTrend: -2.3,
      diversityScore: 82,
    };

    const mockCards: DashboardCardType[] = [
      {
        id: '1',
        title: t('dashboard.metrics.totalEmployees.title'),
        value: DataConsistencyService.getTotalEmployees(),
        change: 2.3,
        changeType: 'positive',
        icon: 'people',
        color: Colors.primary,
        description: 'Click to explore workforce by department, geography, and job levels',
      },
      {
        id: '2',
        title: t('dashboard.metrics.attritionRisk.title'),
        value: '11.4%',
        change: -1.2,
        changeType: 'positive',
        icon: 'trending-down',
        color: Colors.warning,
        description: 'Click to analyze attrition risk patterns and identify at-risk employees',
      },
      {
        id: '3',
        title: t('dashboard.metrics.engagementScore.title'),
        value: 8.1,
        change: 0.4,
        changeType: 'positive',
        icon: 'heart',
        color: Colors.success,
        description: 'Click to drill down into engagement by department and demographics',
      },
      {
        id: '4',
        title: t('dashboard.metrics.leadershipReady.title'),
        value: '73%',
        change: 5.2,
        changeType: 'positive',
        icon: 'school',
        color: Colors.info,
        description: 'Click to explore succession pipeline and leadership readiness',
      },
      {
        id: '5',
        title: t('dashboard.metrics.timeToFill.title'),
        value: '38 days',
        change: -4.0,
        changeType: 'positive',
        icon: 'time',
        color: Colors.accent,
        description: 'Click to analyze hiring efficiency across departments and regions',
      },
      {
        id: '6',
        title: t('dashboard.metrics.diversityScore.title'),
        value: '74%',
        change: 1.8,
        changeType: 'positive',
        icon: 'diversity',
        color: Colors.chartColors.purple,
        description: 'Click to explore diversity metrics and demographic breakdowns',
      },
    ];

    // Apply role-based filtering
    const filteredMetrics = filterTalentMetrics(mockMetrics, currentRole);
    const filteredCards = filterDashboardCards(mockCards, currentRole);

    dispatch(setMetrics(filteredMetrics));
    dispatch(setDashboardCards(filteredCards));
  };

  const getRecommendationsForInsight = (insightType: string) => {
    const recommendations = {
      'engineering-gap': [
        {
          id: '1',
          title: 'Expand Geographic Search Radius',
          description: 'Extend candidate search to include remote workers and candidates from broader geographic areas. This can increase the talent pool by up to 300%.',
          priority: 'high' as const,
          timeline: '2-4 weeks',
          impact: 'High - Immediate access to larger talent pool'
        },
        {
          id: '2',
          title: 'Increase Compensation Package',
          description: 'Market analysis suggests increasing base salary by 15-20% and adding equity components to match competitor offerings.',
          priority: 'high' as const,
          timeline: '1-2 weeks',
          impact: 'High - Significantly improves competitiveness'
        },
        {
          id: '3',
          title: 'Partner with Technical Bootcamps',
          description: 'Establish partnerships with coding bootcamps and accelerated programs to access junior talent that can be trained up.',
          priority: 'medium' as const,
          timeline: '6-8 weeks',
          impact: 'Medium - Long-term pipeline solution'
        },
        {
          id: '4',
          title: 'Implement Employee Referral Program',
          description: 'Launch enhanced referral program with significant bonuses for engineering hires ($5K-10K per successful hire).',
          priority: 'medium' as const,
          timeline: '2-3 weeks',
          impact: 'Medium - Leverages existing network'
        },
        {
          id: '5',
          title: 'Contract-to-Hire Strategy',
          description: 'Use contract positions as a trial period for permanent roles, reducing hiring risk while filling immediate needs.',
          priority: 'low' as const,
          timeline: '1-2 weeks',
          impact: 'Medium - Faster onboarding process'
        }
      ],
      'sales-attrition': [
        {
          id: '1',
          title: 'Conduct Individual Risk Assessments',
          description: 'Schedule one-on-one meetings with identified at-risk employees to understand specific concerns and motivations.',
          priority: 'high' as const,
          timeline: '1-2 weeks',
          impact: 'High - Direct intervention opportunity'
        },
        {
          id: '2',
          title: 'Review and Adjust Sales Targets',
          description: 'Analyze current targets against market conditions and adjust to more achievable levels while maintaining growth.',
          priority: 'high' as const,
          timeline: '3-4 weeks',
          impact: 'High - Reduces stress and burnout'
        },
        {
          id: '3',
          title: 'Implement Flexible Work Arrangements',
          description: 'Offer hybrid work options and flexible scheduling to improve work-life balance for sales team.',
          priority: 'medium' as const,
          timeline: '2-3 weeks',
          impact: 'Medium - Improves satisfaction and retention'
        },
        {
          id: '4',
          title: 'Enhanced Training and Development',
          description: 'Provide advanced sales training, mentorship programs, and clear career progression paths.',
          priority: 'medium' as const,
          timeline: '4-6 weeks',
          impact: 'Medium - Builds engagement and skills'
        },
        {
          id: '5',
          title: 'Team Building and Culture Initiatives',
          description: 'Organize team events, recognition programs, and collaborative projects to strengthen team bonds.',
          priority: 'low' as const,
          timeline: '2-4 weeks',
          impact: 'Low - Improves team morale'
        }
      ],
      'diversity-leadership': [
        {
          id: '1',
          title: 'Launch Leadership Mentorship Program',
          description: 'Pair high-potential diverse employees with current executives for structured mentorship and development.',
          priority: 'high' as const,
          timeline: '4-6 weeks',
          impact: 'High - Direct leadership pipeline development'
        },
        {
          id: '2',
          title: 'Create Accelerated Leadership Track',
          description: 'Develop fast-track development program specifically designed to prepare diverse talent for leadership roles.',
          priority: 'high' as const,
          timeline: '8-12 weeks',
          impact: 'High - Systematic leadership preparation'
        },
        {
          id: '3',
          title: 'Review Promotion Criteria for Bias',
          description: 'Conduct comprehensive audit of promotion processes and criteria to identify and eliminate unconscious bias.',
          priority: 'medium' as const,
          timeline: '6-8 weeks',
          impact: 'High - Systemic process improvement'
        },
        {
          id: '4',
          title: 'Set Diversity Leadership Targets',
          description: 'Establish specific, measurable goals for diverse representation in leadership roles with accountability measures.',
          priority: 'medium' as const,
          timeline: '2-3 weeks',
          impact: 'Medium - Creates clear objectives'
        },
        {
          id: '5',
          title: 'Professional Organization Partnerships',
          description: 'Partner with diverse professional organizations for networking, recruiting, and development opportunities.',
          priority: 'low' as const,
          timeline: '4-6 weeks',
          impact: 'Medium - Expands professional networks'
        }
      ],
      'skills-gap': [
        {
          id: '1',
          title: 'Partner with Online Learning Platforms',
          description: 'Establish corporate partnerships with Coursera, Udacity, and similar platforms for AI/ML certification programs.',
          priority: 'high' as const,
          timeline: '3-4 weeks',
          impact: 'High - Comprehensive skill development'
        },
        {
          id: '2',
          title: 'Allocate Learning and Development Budget',
          description: 'Dedicate specific budget for AI/ML training, certifications, and conference attendance for technical staff.',
          priority: 'high' as const,
          timeline: '2-3 weeks',
          impact: 'High - Enables immediate action'
        },
        {
          id: '3',
          title: 'Internal Knowledge Sharing Sessions',
          description: 'Create regular "Lunch & Learn" sessions where employees share AI/ML knowledge and best practices.',
          priority: 'medium' as const,
          timeline: '2-3 weeks',
          impact: 'Medium - Builds internal expertise'
        },
        {
          id: '4',
          title: 'Hire AI/ML Specialists as Mentors',
          description: 'Bring in senior AI/ML experts to guide and mentor existing technical staff in emerging technologies.',
          priority: 'medium' as const,
          timeline: '6-8 weeks',
          impact: 'High - Expert-led development'
        },
        {
          id: '5',
          title: 'Implement 20% Learning Time',
          description: 'Allow technical staff to spend 20% of work time exploring and learning new AI/ML technologies.',
          priority: 'low' as const,
          timeline: '2-3 weeks',
          impact: 'Medium - Encourages continuous learning'
        }
      ]
    };
    const validInsightTypes = ['engineering-gap', 'sales-attrition', 'diversity-leadership', 'skills-gap'];
    return validInsightTypes.includes(insightType) 
      ? recommendations[insightType as keyof typeof recommendations] 
      : [];
  };

  const handleShowRecommendations = (insightType: string, title: string) => {
    const recommendations = getRecommendationsForInsight(insightType);
    setSelectedInsight({ title, recommendations });
    setModalOpen(true);
  };


  // Show loading state while data loads
  if (loading) {
    return (
      <PowerBICanvas>
        <div className="container">
          <DashboardGridSkeleton />
        </div>
      </PowerBICanvas>
    );
  }

  // Show empty state if no data
  if (!dashboardCards || dashboardCards.length === 0) {
    return (
      <PowerBICanvas>
        <div className="container">
          <NoDataEmptyState onRefresh={loadDashboardData} />
        </div>
      </PowerBICanvas>
    );
  }

  return (
    <PowerBICanvas>
      <RealTimeSyncIndicator />
      <ModernQuickFilters onFiltersChange={() => console.log('Filters changed')} />
      <div className="container">
      
      <UnifiedPageHeader
        title={t('dashboard.title', 'Executive Dashboard')}
        subtitle={t('dashboard.subtitle', 'Real-time HR analytics and performance indicators')}
        metadata={{
          timestamp: new Date(),
          count: dashboardCards?.length || 0,
          status: 'Live'
        }}
        actions={[]}
      />
      
      <ReportSection>
        <PowerBITilesGrid>
          {dashboardCards.map((card, index) => {
            // Map card IDs to KPI types for drill-down functionality
            const getKpiType = (cardId: string): KpiType => {
              switch (cardId) {
                case '1': return 'totalEmployees';
                case '2': return 'attritionRisk';
                case '3': return 'engagementScore';
                case '4': return 'leadershipReady';
                case '5': return 'timeToFill';
                case '6': return 'diversityScore';
                default: return 'totalEmployees';
              }
            };
            
            const getStatusFromChange = (change: number | undefined, changeType: string | undefined) => {
              if (!change || !changeType) return 'warning';
              if (changeType === 'positive') {
                return change > 2 ? 'good' : 'warning';
              } else {
                return 'critical';
              }
            };
            
            const getUnit = (cardId: string): string => {
              switch (cardId) {
                case '2': case '4': case '6': return '%';
                case '5': return ' days';
                default: return '';
              }
            };
            
            return (
              <PowerBITile key={card.id} span={1} onClick={() => setDrilldownModal({ open: true, kpiType: getKpiType(card.id), value: card.value })}>
                <KpiDrillDownCard
                  kpi={getKpiType(card.id)}
                  title={card.title}
                  value={card.value}
                  unit={getUnit(card.id)}
                  change={card.change || 0}
                  changeType={(card.changeType || 'positive') === 'positive' ? 'increase' : 'decrease'}
                  status={getStatusFromChange(card.change, card.changeType)}
                />
              </PowerBITile>
            );
          })}
        </PowerBITilesGrid>
      </ReportSection>


      {/* Role-specific recruitment insights */}
      {currentRole === 'HR_LEADER' && (
        <ReportSection>
          <TalentAcquisitionWidget 
            department={department}
            region="Global"
          />
        </ReportSection>
      )}
      
      {currentRole === 'MANAGER' && (
        <ReportSection>
          <HiringTeamPanel
            department={department}
          />
        </ReportSection>
      )}

      <ReportSection>
        <SectionHeader>
          <SectionTitle>
            Department Performance Analytics
          </SectionTitle>
          <SectionActions>
            <ActionButton>Focus Mode</ActionButton>
            <ActionButton>Show Data</ActionButton>
          </SectionActions>
        </SectionHeader>
        
        <PowerBIChart>
          <ChartTitle>Performance by Department</ChartTitle>
          <ChartContainer>
            <TrendItem>
              <TrendLabel>Engineering</TrendLabel>
              <TrendBar>
                <TrendProgress width="92%" color={Colors.dataViz.series2} />
              </TrendBar>
              <TrendValue><AnimatedCounter value={92} />%</TrendValue>
            </TrendItem>
            <TrendItem>
              <TrendLabel>Sales</TrendLabel>
              <TrendBar>
                <TrendProgress width="87%" color={Colors.dataViz.primary} />
              </TrendBar>
              <TrendValue><AnimatedCounter value={87} />%</TrendValue>
            </TrendItem>
            <TrendItem>
              <TrendLabel>Marketing</TrendLabel>
              <TrendBar>
                <TrendProgress width="79%" color={Colors.dataViz.series4} />
              </TrendBar>
              <TrendValue><AnimatedCounter value={79} />%</TrendValue>
            </TrendItem>
            <TrendItem>
              <TrendLabel>HR</TrendLabel>
              <TrendBar>
                <TrendProgress width="85%" color={Colors.dataViz.series6} />
              </TrendBar>
              <TrendValue><AnimatedCounter value={85} />%</TrendValue>
            </TrendItem>
            <TrendItem>
              <TrendLabel>Finance</TrendLabel>
              <TrendBar>
                <TrendProgress width="88%" color={Colors.dataViz.series1} />
              </TrendBar>
              <TrendValue><AnimatedCounter value={88} />%</TrendValue>
            </TrendItem>
          </ChartContainer>
        </PowerBIChart>
      </ReportSection>
      </div>

      {/* Recommendations Modal */}
      {selectedInsight && (
        <RecommendationsModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          insightTitle={selectedInsight.title}
          recommendations={selectedInsight.recommendations}
        />
      )}
      
      {/* Clean Drill-down Modal */}
      {drilldownModal.open && drilldownModal.kpiType && (
        <CleanDrillDownModal
          kpiType={drilldownModal.kpiType}
          initialValue={drilldownModal.value}
          onClose={() => setDrilldownModal({ open: false, kpiType: null, value: '' })}
        />
      )}
      
      {/* Enhanced Filter Drawer */}
      <EnhancedFilterDrawer 
        onApply={() => {
          // Refresh dashboard data when filters are applied
          console.log('Filters applied, refreshing dashboard...');
        }}
      />
    </PowerBICanvas>
  );
};

export default DashboardScreen;